import { useCallback, useRef, useState } from "react";
import api from "../../services/api";

/**
 * Hook de autosave com debounce.
 *
 * Uso:
 *   const { triggerSave, saveStatus } = useAutoSave(flowId);
 *   // Chamar triggerSave(nodes, edges) a cada mudanca.
 *   // saveStatus: "idle" | "saving" | "saved" | "error"
 */
export default function useAutoSave(flowId, delay = 5000) {
  const [saveStatus, setSaveStatus] = useState("idle");
  const timerRef = useRef(null);
  const lastSavedRef = useRef(null);

  const triggerSave = useCallback(
    (nodes, edges) => {
      if (!flowId) return;
      setSaveStatus("pending");

      if (timerRef.current) clearTimeout(timerRef.current);

      timerRef.current = setTimeout(async () => {
        const payload = JSON.stringify({ nodes, connections: edges });
        if (payload === lastSavedRef.current) {
          setSaveStatus("saved");
          return;
        }
        try {
          setSaveStatus("saving");
          await api.post("/flowbuilder/flow", {
            idFlow: flowId,
            nodes,
            connections: edges,
          });
          lastSavedRef.current = payload;
          setSaveStatus("saved");
        } catch (err) {
          console.error("[AutoSave] error:", err);
          setSaveStatus("error");
        }
      }, delay);
    },
    [flowId, delay]
  );

  const forceSave = useCallback(
    async (nodes, edges) => {
      if (!flowId) return;
      if (timerRef.current) clearTimeout(timerRef.current);
      try {
        setSaveStatus("saving");
        await api.post("/flowbuilder/flow", {
          idFlow: flowId,
          nodes,
          connections: edges,
        });
        lastSavedRef.current = JSON.stringify({ nodes, connections: edges });
        setSaveStatus("saved");
      } catch (err) {
        console.error("[AutoSave] force save error:", err);
        setSaveStatus("error");
      }
    },
    [flowId]
  );

  return { triggerSave, forceSave, saveStatus };
}
